import { useEffect, useMemo } from "react";
import { Sparkles, Store, Bike, ImageOff } from "lucide-react";
import { DESSERT_OF_THE_WEEK } from "../lib/dessertOfTheWeek";
import { api } from "../lib/api";

// Returns "Tue 16 Jun – Sun 21 Jun" for the date range of the feature.
// If `weekStarting` AND `weekEnding` are both provided, those exact dates are used.
// If only `weekStarting` is set, we use that Monday and add 6 days (Mon–Sun).
// If neither is set, we compute the current calendar week's Mon–Sun.
const getWeekRange = (weekStartingIso, weekEndingIso) => {
  let start;
  if (weekStartingIso) {
    const d = new Date(`${weekStartingIso}T00:00:00`);
    if (!Number.isNaN(d.getTime())) start = d;
  }
  if (!start) {
    const today = new Date();
    const dow = today.getDay(); // 0=Sun ... 6=Sat
    const diffToMon = dow === 0 ? -6 : 1 - dow;
    start = new Date(today);
    start.setHours(0, 0, 0, 0);
    start.setDate(today.getDate() + diffToMon);
  }
  let end;
  if (weekEndingIso) {
    const d = new Date(`${weekEndingIso}T00:00:00`);
    if (!Number.isNaN(d.getTime())) end = d;
  }
  if (!end) {
    end = new Date(start);
    end.setDate(start.getDate() + 6);
  }
  const fmt = new Intl.DateTimeFormat("en-NZ", { weekday: "short", day: "numeric", month: "short" });
  return `${fmt.format(start)} – ${fmt.format(end)}`;
};

const isPlaceholderImage = (url) =>
  !url || url.includes("Just%20Dessert%20Me%20Logo") || url.includes("placeholder");

const DessertOfTheWeek = () => {
  const data = DESSERT_OF_THE_WEEK;
  const weekRange = useMemo(() => getWeekRange(data?.weekStarting, data?.weekEnding), [data?.weekStarting, data?.weekEnding]);

  useEffect(() => {
    if (!data?.active) return;
    api.post("/specials/announce", {
      name: data.name,
      description: data.description || null,
      price_label: data.priceLabel || null,
      price_note: data.priceNote || null,
      in_store_only: !!data.inStoreOnly,
      image: isPlaceholderImage(data.image) ? null : data.image,
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data?.active) return null;
  const hasRealImage = !isPlaceholderImage(data.image);
  const isMultiWeek =
    data.weekStarting && data.weekEnding &&
    (new Date(`${data.weekEnding}T00:00:00`) - new Date(`${data.weekStarting}T00:00:00`)) / 86400000 > 7;

  return (
    <section
      data-testid="dessert-of-the-week"
      className="max-w-5xl mx-auto px-6 lg:px-10 pt-2 pb-8"
    >
      <div className="relative rounded-2xl overflow-hidden border border-[#ff1a8c]/50 bg-gradient-to-br from-[#0a0a10] via-[#0a0a10] to-[#1a0510] shadow-[0_0_30px_rgba(255,26,140,0.18)]">
        {/* Top neon strip */}
        <div className="h-[2px] bg-gradient-to-r from-[#ff1a8c] via-[#00b4ff] to-[#ff1a8c]" />

        <div className="grid sm:grid-cols-5 gap-0">
          {/* Image / Placeholder */}
          <div className="sm:col-span-2 relative aspect-[4/3] sm:aspect-auto sm:min-h-[200px]" data-testid="dotw-image-slot">
            {hasRealImage ? (
              <img
                src={data.image}
                alt={data.name}
                loading="lazy"
                className={`absolute inset-0 w-full h-full ${data.imageContain ? "object-contain p-2" : "object-cover"}`}
              />
            ) : (
              <div
                data-testid="dotw-image-placeholder"
                className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-gradient-to-br from-[#1a0510] to-[#0a0a10] border-r border-dashed border-[#ff1a8c]/30"
              >
                <ImageOff className="w-8 h-8 text-[#ff1a8c]/70" />
                <p className="text-[10px] uppercase tracking-[0.25em] text-gray-400">Photo coming soon</p>
              </div>
            )}
          </div>

          {/* Copy */}
          <div className="sm:col-span-3 p-5 sm:p-7 flex flex-col justify-center">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border border-[#ff1a8c]/60 text-[9px] uppercase tracking-[0.28em] text-[#ff1a8c] w-fit mb-2.5">
              <Sparkles className="w-2.5 h-2.5" />
              Dessert of the Week
            </span>

            <h2
              data-testid="dotw-name"
              className="font-script text-3xl sm:text-4xl neon-gradient-text leading-[1] mb-2"
            >
              {data.name}
            </h2>

            <p
              data-testid="dotw-description"
              className="text-gray-300 text-sm leading-relaxed max-w-xl"
            >
              {data.description}
            </p>

            <div className="flex flex-wrap items-center gap-2 mt-4">
              {data.priceLabel && (
                <span
                  data-testid="dotw-price"
                  className="inline-flex items-center text-sm font-bold neon-blue px-2.5 py-0.5 rounded-full border border-[#00b4ff]/50 bg-[#00b4ff]/10"
                >
                  {data.priceLabel}
                </span>
              )}
              {data.priceNote && (
                <span
                  data-testid="dotw-price-note"
                  className="text-[10px] uppercase tracking-[0.18em] text-gray-400"
                >
                  {data.priceNote}
                </span>
              )}
              <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.2em] text-gray-300 px-2 py-0.5 rounded-full border border-white/15">
                <Store className="w-2.5 h-2.5 text-[#ff1a8c]" /> In store{data.inStoreOnly ? " only" : ""}
              </span>
              {!data.inStoreOnly && (
                <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.2em] text-gray-300 px-2 py-0.5 rounded-full border border-white/15">
                  <Bike className="w-2.5 h-2.5 text-[#00b4ff]" /> On delivery platforms
                </span>
              )}
            </div>

            <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] uppercase tracking-[0.24em] text-gray-500 mt-4">
              <span>{isMultiWeek ? "Limited time" : "This week only"}</span>
              <span className="text-gray-700">·</span>
              <span data-testid="dotw-week-range" className="text-[#00b4ff]/80">{weekRange}</span>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DessertOfTheWeek;
